<div class="otp-dialog">

    <!-- Shared shield header (subtitle tracks the current screen) -->
    <div class="dialog-header">
        <div class="shield-icon">
            <i class="ph-bold ph-shield-check" aria-hidden="true"></i>
        </div>
        <div class="dialog-title">Additional Verification Required</div>
        <div class="dialog-subtitle">{{ subtitle }}</div>
    </div>

    <!-- Type selection screen -->
    <ng-container *ngIf="!type">
        <div class="dialog-body">
            <div class="type-buttons">
                <button type="button" e2eId="confirm-2fa-method-sms" [disabled]="isSendingSms" (click)="setSmsType()">
                    <i class="ph-bold ph-chat-circle-text" aria-hidden="true"></i>
                    <span>
                        <strong>{{ isSendingSms ? 'Sending code...' : 'Text me a code' }}</strong>
                        <small>Receive a code via SMS</small>
                    </span>
                </button>
                <button type="button" e2eId="confirm-2fa-method-google" [disabled]="isSendingSms" (click)="setGoogleType()">
                    <i class="ph-bold ph-lock-key" aria-hidden="true"></i>
                    <span>
                        <strong>Authenticator app</strong>
                        <small>Use your authenticator app</small>
                    </span>
                </button>
            </div>
        </div>

        <div class="dialog-footer">
            <button type="button" class="btn-cancel" mat-dialog-close>Cancel</button>
        </div>
    </ng-container>

    <!-- OTP entry screen -->
    <ng-container *ngIf="type && type !== 'recovery'">
        <div class="dialog-body">
            <!-- OTP cells (shared control) -->
            <app-otp-code-input
                    [disabled]="isSubmitting || isSuccess"
                    [error]="hasError"
                    [success]="isSuccess"
                    (valueChange)="onCodeChange($event)"
                    (completed)="submit($event)"></app-otp-code-input>

            <div class="otp-hint" [class.otp-hint-error]="hasError" [class.otp-hint-success]="isSuccess">{{ hintText }}</div>

            <!-- Validity countdown / expired state + resend (SMS only) -->
            <div class="otp-resend-row" *ngIf="showResendRow">
                <span class="otp-resend-row__status" [class.otp-resend-row__status--expired]="isExpired || smsSendFailed">
                    {{ resendStatus }}
                </span>
                <span class="otp-resend-row__prompt">
                    Didn't get a code?
                    <button type="button"
                            class="otp-resend"
                            e2eId="confirm-2fa-resend"
                            [disabled]="isResending || isSubmitting || isSuccess"
                            (click)="resendCode()">{{ isResending ? 'Sending…' : 'Resend code' }}</button>
                </span>
            </div>

            <!-- Trust-device -->
            <ng-container *ngIf="showTrustDevice">
                <mat-divider></mat-divider>
                <mat-checkbox e2eId="confirm-2fa-trust-device" [(ngModel)]="trustDevice" [disableRipple]="true">Trust this browser</mat-checkbox>
                <p>Don't ask for a code again on this browser for 30 days. Only use on personal devices.</p>
            </ng-container>

            <!-- Recovery-code fallback — hidden when the caller knows the user holds no codes (SD-8982) -->
            <button type="button"
                    *ngIf="canUseRecoveryCode"
                    mat-flat-button
                    linkButton
                    [disableRipple]="true"
                    class="recovery-link"
                    e2eId="confirm-2fa-use-recovery"
                    [disabled]="isSubmitting || isSuccess"
                    (click)="setRecoveryType()">{{ 'Use a recovery code instead' }}</button>
        </div>

        <div class="dialog-footer">
            <button type="button" class="btn-cancel" *ngIf="canGoBack" (click)="goBack()">Back</button>
            <button type="button" class="btn-cancel" *ngIf="!canGoBack" mat-dialog-close>Cancel</button>
            <flex-spacer></flex-spacer>
            <button type="button"
                    class="btn-verify"
                    e2eId="confirm-2fa-submit"
                    [class.btn-verify--success]="isSuccess"
                    [disabled]="code.length < 6 || isSubmitting || isSuccess"
                    (click)="submit(code)">
                <mat-spinner *ngIf="isSubmitting && !isSuccess" class="btn-verify__spinner" diameter="16"></mat-spinner>
                <span *ngIf="!isSubmitting && !isSuccess">Verify</span>
                <span *ngIf="isSubmitting && !isSuccess">Verifying…</span>
                <span *ngIf="isSuccess">Verified</span>
            </button>
        </div>
    </ng-container>

    <!-- Recovery-code entry screen -->
    <ng-container *ngIf="type === 'recovery'">
        <div class="dialog-body">
            <!-- Codes are 10 chars from an unambiguous alphanumeric alphabet (RecoveryCodeService),
                 shown as XXXXX-XXXXX. `A` is ngx-mask's alphanumeric token; the separator is
                 dropped from the model (its default) so `recoveryCode` stays 10 raw characters and
                 the length guard in submitRecovery() keeps working. The server re-inserts the dash
                 in normalize(), so either form validates. -->
            <input type="text"
                   class="recovery-code-input"
                   mask="AAAAA-AAAAA"
                   e2eId="confirm-2fa-recovery-input"
                   aria-label="Recovery code"
                   placeholder="XXXXX-XXXXX"
                   autocomplete="one-time-code"
                   [(ngModel)]="recoveryCode"
                   [disabled]="isSubmitting || isSuccess"
                   (keyup.enter)="submitRecovery()" />

            <div class="otp-hint" [class.otp-hint-error]="hasError" [class.otp-hint-success]="isSuccess">{{ recoveryHint }}</div>

            <!-- Trust-device -->
            <ng-container *ngIf="showTrustDevice">
                <mat-divider></mat-divider>
                <mat-checkbox [(ngModel)]="trustDevice" [disableRipple]="true">Trust this browser</mat-checkbox>
                <p>Don't ask for a code again on this browser for 30 days. Only use on personal devices.</p>
            </ng-container>
        </div>

        <div class="dialog-footer">
            <button type="button" class="btn-cancel" (click)="backFromRecovery()">Back</button>
            <flex-spacer></flex-spacer>
            <button type="button"
                    class="btn-verify"
                    e2eId="confirm-2fa-recovery-submit"
                    [class.btn-verify--success]="isSuccess"
                    [disabled]="recoveryCode.trim().length < 10 || isSubmitting || isSuccess"
                    (click)="submitRecovery()">
                <mat-spinner *ngIf="isSubmitting && !isSuccess" class="btn-verify__spinner" diameter="16"></mat-spinner>
                <span *ngIf="!isSubmitting && !isSuccess">Verify</span>
                <span *ngIf="isSubmitting && !isSuccess">Verifying…</span>
                <span *ngIf="isSuccess">Verified</span>
            </button>
        </div>
    </ng-container>

</div>
